import React from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import PublicHeroPageLayout from '../components/PublicHeroPageLayout';

const LEGAL_CONTENT_REVISION_ISO = '2026-05-01';

function formatLegalLastUpdated(lang) {
  const d = new Date(`${LEGAL_CONTENT_REVISION_ISO}T12:00:00`);
  try {
    return new Intl.DateTimeFormat(lang === 'es' ? 'es-DO' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return LEGAL_CONTENT_REVISION_ISO;
  }
}

const legalDocuments = {
  terms: {
    en: {
      title: 'Terms and Conditions',
      content: `1. Introduction
Welcome to Quisqueya Quest. By using our platform, you agree to these terms and conditions.

2. Definitions
- "Platform" refers to Quisqueya Quest's website and mobile applications
- "User" refers to any person who accesses or uses the Platform
- "Tour Operator" refers to verified providers offering tours through our Platform
- "Content" refers to all information, text, images, data, or other materials uploaded to or displayed on the Platform

3. Platform Usage
3.1. Users must:
- Be at least 18 years old
- Provide accurate information
- Maintain account security
- Comply with all applicable laws

4. Tour Bookings and Cancellations
4.1. Booking Process:
- All bookings are subject to availability
- Prices are in specified currency and include applicable taxes
- Payment is required to confirm booking

4.2. Cancellation and Refund Policy:
- Users may cancel their bookings at any time through the Platform
- Refund policies are set and managed exclusively by individual Tour Operators
- Quisqueya Quest does not process or handle refunds directly
- Users must contact their Tour Operator directly for refund requests and policies
- Platform fees are non-refundable
- Each Tour Operator maintains their own cancellation and refund terms
- We strongly recommend reviewing the specific Tour Operator's refund policy before booking

5. User Responsibilities
- Respect local customs and laws
- Arrive on time for tours
- Follow tour operator instructions
- Maintain appropriate behavior
- Provide accurate contact information

6. Intellectual Property and Content
6.1. Platform Content:
- Quisqueya Quest owns all intellectual property rights to the Platform and its content
- All content uploaded to the Platform becomes the property of Quisqueya Quest
- Users grant Quisqueya Quest a worldwide license to use any content they upload
- Tour operators grant Quisqueya Quest rights to use their tour information and images

6.2. Exceptions:
- Users retain ownership of their personal information (full name, address, email, phone number)
- Personal information is protected under our Privacy Policy

7. Privacy and Data Usage
7.1. Personal Information:
- We DO NOT sell user personal information
- Tour Operators have access to booked users' contact information
- This information is shared only for the purpose of tour operation
- Users consent to sharing their contact details with booked Tour Operators

8. Liability
8.1. Platform Liability:
- We act as an intermediary between users and tour operators
- We are not responsible for tour operator services
- We maintain reasonable security measures
- We are not liable for any disputes between users and tour operators

9. Changes to Terms
We reserve the right to modify these terms with notice to users.

10. Contact
For questions about these terms, contact us at contact@quisqueyaquest.com`,
    },
    es: {
      title: 'Términos y Condiciones',
      content: `1. Introducción
Bienvenido a Quisqueya Quest. Al utilizar nuestra plataforma, aceptas estos términos y condiciones.

2. Definiciones
- "Plataforma" se refiere al sitio web y aplicaciones móviles de Quisqueya Quest
- "Usuario" se refiere a cualquier persona que accede o utiliza la Plataforma
- "Operador Turístico" se refiere a proveedores verificados que ofrecen tours a través de nuestra Plataforma
- "Contenido" se refiere a toda la información, texto, imágenes, datos u otros materiales cargados o mostrados en la Plataforma

3. Uso de la Plataforma
3.1. Los usuarios deben:
- Tener al menos 18 años
- Proporcionar información precisa
- Mantener la seguridad de la cuenta
- Cumplir con todas las leyes aplicables

4. Reservas y Cancelaciones de Tours
4.1. Proceso de Reserva:
- Todas las reservas están sujetas a disponibilidad
- Los precios están en la moneda especificada e incluyen impuestos aplicables
- Se requiere pago para confirmar la reserva

4.2. Política de Cancelación y Reembolso:
- Los usuarios pueden cancelar sus reservas en cualquier momento a través de la Plataforma
- Las políticas de reembolso son establecidas y gestionadas exclusivamente por los Operadores Turísticos
- Quisqueya Quest no procesa ni maneja reembolsos directamente
- Los usuarios deben contactar directamente a su Operador Turístico para solicitudes y políticas de reembolso
- Las tarifas de la plataforma no son reembolsables
- Cada Operador Turístico mantiene sus propios términos de cancelación y reembolso
- Recomendamos revisar la política de reembolso específica del Operador Turístico antes de reservar

5. Responsabilidades del Usuario
- Respetar las costumbres y leyes locales
- Llegar a tiempo a los tours
- Seguir las instrucciones del operador turístico
- Mantener un comportamiento apropiado
- Proporcionar información de contacto precisa

6. Propiedad Intelectual y Contenido
6.1. Contenido de la Plataforma:
- Quisqueya Quest posee todos los derechos de propiedad intelectual de la Plataforma y su contenido
- Todo el contenido cargado en la Plataforma se convierte en propiedad de Quisqueya Quest
- Los usuarios otorgan a Quisqueya Quest una licencia mundial para usar cualquier contenido que suban
- Los operadores turísticos otorgan a Quisqueya Quest derechos para usar su información e imágenes de tours

6.2. Excepciones:
- Los usuarios mantienen la propiedad de su información personal (nombre completo, dirección, correo electrónico, número de teléfono)
- La información personal está protegida bajo nuestra Política de Privacidad

7. Privacidad y Uso de Datos
7.1. Información Personal:
- NO vendemos información personal de los usuarios
- Los Operadores Turísticos tienen acceso a la información de contacto de los usuarios reservados
- Esta información se comparte solo con el propósito de operación del tour
- Los usuarios consienten compartir sus datos de contacto con los Operadores Turísticos reservados

8. Responsabilidad
8.1. Responsabilidad de la Plataforma:
- Actuamos como intermediarios entre usuarios y operadores turísticos
- No somos responsables por los servicios de los operadores turísticos
- Mantenemos medidas de seguridad razonables
- No somos responsables de disputas entre usuarios y operadores turísticos

9. Cambios en los Términos
Nos reservamos el derecho de modificar estos términos con aviso a los usuarios.

10. Contacto
Para preguntas sobre estos términos, contáctanos en contact@quisqueyaquest.com`,
    },
  },
  privacy: {
    en: {
      title: 'Privacy Policy',
      content: `1. Introduction
At Quisqueya Quest, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information. If you are a resident of the European Economic Area (EEA), you have specific rights under the GDPR (General Data Protection Regulation).

2. Data Controller
Quisqueya Quest acts as the data controller for the personal information you provide. You can contact us at contact@quisqueyaquest.com.

3. Legal Basis for Processing
We process your personal data under the following legal bases:
- Contract fulfillment (for booking services)
- Legal obligation (for tax and business records)
- Legitimate interests (for improving our services)
- Your consent (for marketing communications)

4. Information We Collect
4.1. Information You Provide:
- Name and contact information
- Account credentials
- Travel preferences and bookings
- User-generated content

4.2. Payment Information:
- We DO NOT collect or store payment card details
- All payments are processed securely by PayPal
- We only store transaction records (booking reference, amount, date)
- PayPal's privacy policy governs the handling of your payment information

4.3. Automatically Collected Information:
- Device information
- IP address
- Browser type
- Usage data
- Cookies and similar technologies

5. How We Use Your Information
Each use of your data is based on a specific legal basis:
- Process your bookings (Contract fulfillment)
- Provide customer support (Contract fulfillment)
- Send booking confirmations and updates (Contract fulfillment)
- Improve our services (Legitimate interest)
- Personalize your experience (Legitimate interest)
- Send marketing communications (Consent)
- Comply with legal obligations (Legal obligation)

6. Information Sharing
6.1. We Share With:
- Tour operators (only for booked tours: name and contact details)
- PayPal (for payment processing only)
- Service providers (analytics, customer support)
- Legal authorities when required

6.2. International Transfers:
- Data may be transferred outside the EEA
- We ensure appropriate safeguards through Standard Contractual Clauses
- You can request a copy of these safeguards by contacting us

6.3. We DO NOT:
- Collect or store payment card information
- Sell your personal information
- Share your data with unauthorized third parties
- Use your information for unrelated marketing

7. Your GDPR Rights
As an EEA resident, you have the right to:
- Access your personal data
- Rectify inaccurate data
- Erase your data ("right to be forgotten")
- Restrict processing
- Data portability
- Object to processing
- Withdraw consent
- Lodge a complaint with a supervisory authority

To exercise these rights, contact us at contact@quisqueyaquest.com. We will respond within 30 days.

8. Data Retention
- Active account data: Retained while account is active
- Transaction records: 7 years (legal requirement)
- Marketing preferences: Until consent withdrawal
- Inactive accounts: Deleted after 2 years of inactivity

9. Data Security
- Industry-standard security measures
- Regular security assessments
- Encrypted data transmission
- Secure data storage
- Payment security is handled by PayPal
- Data breach notification within 72 hours (if applicable)

10. Cookie Management
We use cookies for:
- Essential functionality
- Performance monitoring
- User experience improvement
- Marketing (with consent)

You can manage cookie preferences through our cookie banner or browser settings.

11. Children's Privacy
- Service not intended for users under 18
- No intentional collection of minor's data
- Immediate deletion if discovered

12. Changes to This Policy
- We may update this policy
- Notification of material changes
- Continued use means acceptance
- Previous versions available upon request

13. Data Protection Officer
For privacy inquiries or to exercise your rights:
Email: contact@quisqueyaquest.com
Response time: Within 30 days

14. Supervisory Authority
You have the right to lodge a complaint with your local data protection authority.`,
    },
    es: {
      title: 'Política de Privacidad',
      content: `1. Introducción
En Quisqueya Quest, nos tomamos en serio tu privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos tu información. Si eres residente del Espacio Económico Europeo (EEE), tienes derechos específicos bajo el RGPD (Reglamento General de Protección de Datos).

2. Responsable del Tratamiento
Quisqueya Quest actúa como responsable del tratamiento de la información personal que proporcionas. Puedes contactarnos en contact@quisqueyaquest.com.

3. Base Legal para el Tratamiento
Procesamos tus datos personales bajo las siguientes bases legales:
- Ejecución del contrato (para servicios de reserva)
- Obligación legal (para registros fiscales y comerciales)
- Intereses legítimos (para mejorar nuestros servicios)
- Tu consentimiento (para comunicaciones de marketing)

4. Información que Recopilamos
4.1. Información que Proporcionas:
- Nombre e información de contacto
- Credenciales de cuenta
- Preferencias de viaje y reservas
- Contenido generado por usuarios

4.2. Información de Pago:
- NO recopilamos ni almacenamos detalles de tarjetas de pago
- Todos los pagos son procesados de forma segura por PayPal
- Solo almacenamos registros de transacciones (referencia de reserva, monto, fecha)
- La política de privacidad de PayPal rige el manejo de tu información de pago

4.3. Información Recopilada Automáticamente:
- Información del dispositivo
- Dirección IP
- Tipo de navegador
- Datos de uso
- Cookies y tecnologías similares

5. Cómo Usamos tu Información
Cada uso de tus datos se basa en una base legal específica:
- Procesar tus reservas (Ejecución del contrato)
- Proporcionar atención al cliente (Ejecución del contrato)
- Enviar confirmaciones y actualizaciones (Ejecución del contrato)
- Mejorar nuestros servicios (Interés legítimo)
- Personalizar tu experiencia (Interés legítimo)
- Enviar comunicaciones de marketing (Consentimiento)
- Cumplir con obligaciones legales (Obligación legal)

6. Compartir Información
6.1. Compartimos Con:
- Operadores turísticos (solo para tours reservados: nombre y datos de contacto)
- PayPal (solo para procesamiento de pagos)
- Proveedores de servicios (análisis, atención al cliente)
- Autoridades legales cuando sea requerido

6.2. Transferencias Internacionales:
- Los datos pueden transferirse fuera del EEE
- Garantizamos salvaguardas apropiadas mediante Cláusulas Contractuales Estándar
- Puedes solicitar una copia de estas salvaguardas contactándonos

6.3. NO hacemos:
- Recopilar o almacenar información de tarjetas de pago
- Vender tu información personal
- Compartir tus datos con terceros no autorizados
- Usar tu información para marketing no relacionado

7. Tus Derechos RGPD
Como residente del EEE, tienes derecho a:
- Acceder a tus datos personales
- Rectificar datos inexactos
- Borrar tus datos ("derecho al olvido")
- Restringir el tratamiento
- Portabilidad de datos
- Oponerte al tratamiento
- Retirar el consentimiento
- Presentar una reclamación ante una autoridad de control

Para ejercer estos derechos, contáctanos en contact@quisqueyaquest.com. Responderemos en 30 días.

8. Retención de Datos
- Datos de cuenta activa: Retenidos mientras la cuenta esté activa
- Registros de transacciones: 7 años (requisito legal)
- Preferencias de marketing: Hasta retirada del consentimiento
- Cuentas inactivas: Eliminadas después de 2 años de inactividad

9. Seguridad de Datos
- Medidas de seguridad estándar de la industria
- Evaluaciones regulares de seguridad
- Transmisión de datos encriptada
- Almacenamiento seguro de datos
- La seguridad de pagos es manejada por PayPal
- Notificación de violación de datos en 72 horas (si aplica)

10. Gestión de Cookies
Usamos cookies para:
- Funcionalidad esencial
- Monitoreo de rendimiento
- Mejora de experiencia de usuario
- Marketing (con consentimiento)

Puedes gestionar las preferencias de cookies a través de nuestro banner o configuración del navegador.

11. Privacidad de Menores
- Servicio no destinado a usuarios menores de 18 años
- Sin recopilación intencional de datos de menores
- Eliminación inmediata si se descubren

12. Cambios en esta Política
- Podemos actualizar esta política
- Notificación de cambios materiales
- El uso continuado significa aceptación
- Versiones anteriores disponibles bajo solicitud

13. Delegado de Protección de Datos
Para consultas de privacidad o ejercer tus derechos:
Email: contact@quisqueyaquest.com
Tiempo de respuesta: Dentro de 30 días

14. Autoridad de Control
Tienes derecho a presentar una reclamación ante tu autoridad local de protección de datos.`,
    },
  },
  cookies: {
    en: {
      title: 'Cookie Policy',
      content: `1. What Are Cookies
Cookies are small text files stored on your device when you visit our website. They help us provide and improve our services.

2. Types of Cookies We Use
2.1. Essential Cookies:
- Required for basic website functionality
- Cannot be disabled
- Used for authentication and security

2.2. Functional Cookies:
- Remember your preferences
- Improve your browsing experience
- Store language preferences

2.3. Analytics Cookies:
- Help us understand how visitors use our site
- Measure website performance
- Improve our services

2.4. Marketing Cookies:
- Track your browsing habits
- Deliver targeted advertising
- Measure marketing effectiveness

3. How We Use Cookies
- Authenticate users
- Remember your preferences
- Analyze site traffic
- Improve user experience
- Personalize content
- Secure your data

4. Third-Party Cookies
We use cookies from:
- Analytics providers
- Payment processors
- Social media platforms
- Advertising partners

5. Cookie Management
You can:
- Accept or decline non-essential cookies
- Modify browser settings to control cookies
- Delete existing cookies
- Set cookie preferences in our banner

6. Impact of Disabling Cookies
Disabling cookies may:
- Limit website functionality
- Affect user experience
- Disable certain features
- Prevent personalization

7. Updates to This Policy
- We may update this policy
- Changes will be posted here
- Continue using site means acceptance

8. Contact Us
For cookie-related questions, contact us at contact@quisqueyaquest.com`,
    },
    es: {
      title: 'Política de Cookies',
      content: `1. Qué Son las Cookies
Las cookies son pequeños archivos de texto almacenados en tu dispositivo cuando visitas nuestro sitio web. Nos ayudan a proporcionar y mejorar nuestros servicios.

2. Tipos de Cookies que Utilizamos
2.1. Cookies Esenciales:
- Requeridas para la funcionalidad básica del sitio
- No se pueden deshabilitar
- Usadas para autenticación y seguridad

2.2. Cookies Funcionales:
- Recuerdan tus preferencias
- Mejoran tu experiencia de navegación
- Almacenan preferencias de idioma

2.3. Cookies Analíticas:
- Nos ayudan a entender cómo los visitantes usan nuestro sitio
- Miden el rendimiento del sitio web
- Mejoran nuestros servicios

2.4. Cookies de Marketing:
- Rastrean tus hábitos de navegación
- Entregan publicidad dirigida
- Miden la efectividad del marketing

3. Cómo Usamos las Cookies
- Autenticar usuarios
- Recordar tus preferencias
- Analizar el tráfico del sitio
- Mejorar la experiencia del usuario
- Personalizar contenido
- Asegurar tus datos

4. Cookies de Terceros
Usamos cookies de:
- Proveedores de análisis
- Procesadores de pago
- Plataformas de redes sociales
- Socios publicitarios

5. Gestión de Cookies
Puedes:
- Aceptar o rechazar cookies no esenciales
- Modificar la configuración del navegador
- Eliminar cookies existentes
- Establecer preferencias en nuestro banner

6. Impacto de Deshabilitar Cookies
Deshabilitar cookies puede:
- Limitar la funcionalidad del sitio
- Afectar la experiencia del usuario
- Deshabilitar ciertas funciones
- Prevenir la personalización

7. Actualizaciones de esta Política
- Podemos actualizar esta política
- Los cambios se publicarán aquí
- Continuar usando el sitio significa aceptación

8. Contáctanos
Para preguntas sobre cookies, contáctanos en contact@quisqueyaquest.com`,
    },
  },
};

const LegalDocumentPage = () => {
  const { document } = useParams();
  const { language } = useLanguage();

  const lang = language === 'es' ? 'es' : 'en';
  const doc = legalDocuments[document];
  const content = doc ? (doc[lang] || doc['en']) : null;
  const lastUpdated = formatLegalLastUpdated(lang);
  const labels = lang === 'en'
    ? { lastUpdated: 'Last updated:' }
    : { lastUpdated: 'Última actualización:' };

  return (
    <PublicHeroPageLayout>
      {!content ? (
        <div className="rounded-xl border border-red-400/40 bg-red-950/30 px-4 py-6 text-center text-red-100">
          {lang === 'en' ? 'Document not found.' : 'Documento no encontrado.'}
        </div>
      ) : (
        <article className="max-w-4xl">
          <h1 className="mb-4 font-serif text-3xl font-medium tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)] sm:text-4xl">
            {content.title}
          </h1>
          <p className="mb-8 text-sm text-white/70">
            {labels.lastUpdated} {lastUpdated}
          </p>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-white/90 sm:text-base [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:font-serif [&_h2]:text-xl [&_h2]:text-white [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1">
            {content.content}
          </div>
        </article>
      )}
    </PublicHeroPageLayout>
  );
};

export default LegalDocumentPage;
